'use client';

import { cn, truncateAddress } from '@/lib/utils';
import {
  useGlobalActivity,
  type GlobalActivityItem,
} from '@/hooks/use-global-activity';
import { HV_TAKEOVER_GRID_ID, isHiddenV21Grid } from '@/lib/constants/addresses';

/**
 * Inter-section activity ticker (Figma node `18:1294` / Group 19).
 *
 * Edge-to-edge horizontal strip of pills (`alice.eth took over PEPE/USD`,
 * `bob.base.eth claimed Boardroom`, …) sitting between Section 3
 * (Ownership) and Section 4 (Own it. Or lose it.). Hooked to the live
 * global activity feed via `useGlobalActivity` — the same source the
 * in-app lobby ticker uses — showing the latest ~20 meaningful events.
 *
 * Animation: a doubled list slid `translateX(-50%)` via the
 * `animate-marquee-x` keyframe registered in `tailwind.config.js`. The
 * outer `overflow-hidden` clips at the viewport edges; the inner row
 * uses `w-max` so the keyframe travels its intrinsic content width.
 * `motion-reduce:animate-none` honours the user's reduced-motion
 * preference by parking the strip.
 *
 * The strip is intentionally rendered outside the `max-w-7xl`
 * containers used by its neighbouring sections so it bleeds the full
 * viewport width — that's the Figma's "full-bleed band" treatment.
 */
export function ActivityTicker() {
  const { items, isLoading } = useGlobalActivity(20);

  const pills = items
    .map(toPill)
    .filter((p): p is Pill => p !== null)
    .slice(0, 20);

  // Nothing to show yet (cold cache, or a genuinely idle network):
  // render the band with a subtle skeleton rather than collapsing the
  // layout, so the section spacing stays stable.
  const showSkeleton = isLoading || pills.length === 0;

  return (
    <div
      aria-hidden
      className="relative isolate overflow-hidden border-y border-amber-500/15 bg-black/70 py-4"
    >
      {/* Edge fade so pills dissolve into the surrounding section
          background instead of clipping at a hard edge. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-black to-transparent sm:w-32"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-black to-transparent sm:w-32"
      />

      {showSkeleton ? (
        <div className="flex w-max items-center gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="inline-block h-[34px] w-40 shrink-0 animate-pulse rounded-[4px] bg-zinc-900/80"
            />
          ))}
        </div>
      ) : (
        <div className="flex w-max items-center gap-3 animate-marquee-x motion-reduce:animate-none">
          {/* Double the list so `translateX(-50%)` lines copy A's end up
              with copy B's start. The loop is seamless because both
              halves are identical. */}
          {[...pills, ...pills].map((item, i) => (
            <ActivityPill key={i} {...item} />
          ))}
        </div>
      )}
    </div>
  );
}

type ActivityTone = 'aggressive' | 'reward' | 'neutral';

interface Pill {
  actor: string;
  verb: string;
  target: string;
  tone: ActivityTone;
}

/**
 * Map a live `GlobalActivityItem` to a marketing-ticker pill, or `null`
 * for events we don't surface here (price tweaks, transfers, etc.).
 * Mirrors the lobby ticker's event vocabulary but in the friendlier
 * "{actor} {verb} {target}" phrasing the landing uses.
 */
function toPill(item: GlobalActivityItem): Pill | null {
  if (!item.gridId || isHiddenV21Grid(item.gridId)) return null;

  const actor = item.userIdentity?.displayName || truncateAddress(item.user);
  const target =
    item.gridId === HV_TAKEOVER_GRID_ID
      ? 'Boardroom'
      : item.coinSymbol || item.coinName || truncateAddress(item.poolId);

  switch (item.eventType) {
    case 'BOUGHT':
      return item.isTakeover
        ? { actor, verb: 'took over', target, tone: 'aggressive' }
        : { actor, verb: 'bought', target, tone: 'aggressive' };
    case 'AUCTION_BUY':
      return { actor, verb: 'bought', target, tone: 'aggressive' };
    case 'CLAIMED':
    case 'SEAT_CLAIMED':
      return { actor, verb: 'claimed', target, tone: 'reward' };
    case 'FEES_CLAIMED':
    case 'TAX_CLAIMED':
      return { actor, verb: 'harvested', target, tone: 'reward' };
    case 'FORFEITED':
      return { actor, verb: 'forfeited', target, tone: 'neutral' };
    case 'ABANDONED':
      return { actor, verb: 'left', target, tone: 'neutral' };
    case 'LAUNCHED':
    case 'GRID_LAUNCHED':
      return { actor, verb: 'launched', target, tone: 'neutral' };
    default:
      return null;
  }
}

const TONE_COLOR: Record<ActivityTone, string> = {
  // Aggressive flips read in amber — same hue the buyout / takeover
  // animations use across the lobby.
  aggressive: 'text-amber-400',
  // Reward harvests in emerald, mirroring `text-positive` in
  // `globals.css` and the in-app claim animations.
  reward: 'text-emerald-400',
  // Everything else (forfeits, launches, exits) in sky-blue.
  neutral: 'text-sky-300',
};

function ActivityPill({ actor, verb, target, tone }: Pill) {
  // The whole pill — actor, verb, and target — takes the event's hue so
  // each event reads as a single coloured statement.
  return (
    <span
      className={cn(
        'inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap',
        'rounded-[4px] border border-amber-500/20 bg-zinc-950/80 px-4',
        'font-mono text-[12px] tracking-[0.02em]',
        TONE_COLOR[tone],
      )}
    >
      <span>{actor}</span>
      <span>{verb}</span>
      <span>{target}</span>
    </span>
  );
}
